import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDoctorConsultationResult, getPatientConsultationResult } from '../services/consultationApi';

const DOC_TYPE_META = {
  SCAN:         { label: 'Scan',         icon: '🔬', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  REPORT:       { label: 'Report',       icon: '📋', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  PRESCRIPTION: { label: 'Prescription', icon: '💊', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
};

const ReadOnlyField = ({ label, value }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
      letterSpacing: '0.08em', fontFamily: 'DM Sans, sans-serif', marginBottom: 8,
    }}>
      {label}
    </div>
    <div style={{
      background: '#f8fafc', border: '1px solid #e8e4ff', borderRadius: 12,
      padding: '14px 16px', fontSize: 14, color: value ? '#1e1b4b' : '#9ca3af',
      fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6, minHeight: 52,
      whiteSpace: 'pre-wrap',
    }}>
      {value || 'Not recorded'}
    </div>
  </div>
);

const ConsultationResultPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPatient = ['PATIENT', 'patient'].includes(user?.role);

  const [record, setRecord]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = isPatient
          ? await getPatientConsultationResult(appointmentId)
          : await getDoctorConsultationResult(appointmentId);
        setRecord(data);
      } catch (e) {
        setError(e?.response?.data?.message || 'Could not load consultation record.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [appointmentId, isPatient]);

  const submittedAt = record?.createdAt
    ? new Date(record.createdAt).toLocaleString(undefined, {
        weekday: 'long', year: 'numeric', month: 'long',
        day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

  return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: '100%', padding: '24px 24px 40px' }}>

        {/* Back button */}
        <button
          onClick={() => navigate(isPatient ? '/appointments' : '/doctor/dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, color: '#6b7280',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif', padding: '0 0 20px 0',
            transition: 'color .12s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#4f46e5'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        >
          ← Back to {isPatient ? 'Appointments' : 'Dashboard'}
        </button>

        {/* Page header */}
        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>🩺 Consultation Record</h1>
          {!loading && !error && (
            <p className="t-body-sm">Submitted {submittedAt}</p>
          )}
        </div>

        {/* Loading skeletons */}
        {loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 16, marginTop: 8,
          }}>
            {[200, 160, 180, 240].map((h, i) => (
              <div key={i} className="skeleton" style={{ height: h, borderRadius: 16 }} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="error-state">
            <span>⚠️</span> {error}
            <button
              className="t-link"
              style={{ marginLeft: 12 }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content — two column on desktop */}
        {record && !loading && (
          <div
            className="fade-up-1"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 20,
              alignItems: 'start',
            }}
          >
            {/* ── Left column: clinical info ── */}
            <div className="card" style={{ padding: '28px 28px 24px' }}>
              {/* Attendance badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', borderRadius: 9999, marginBottom: 28,
                background: record.patientAttended ? '#ecfdf5' : '#fef2f2',
                border: `1px solid ${record.patientAttended ? '#6ee7b7' : '#fca5a5'}`,
                color: record.patientAttended ? '#059669' : '#dc2626',
                fontSize: 13, fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
              }}>
                {record.patientAttended ? '✅ Patient Attended' : '⚠️ Patient Did Not Attend'}
              </div>

              <ReadOnlyField label="Existing Conditions" value={record.existingConditions} />
              <ReadOnlyField label="Doctor's Analysis"   value={record.analysis} />
              <ReadOnlyField label="Outcome / Result"    value={record.result} />
            </div>

            {/* ── Right column: documents ── */}
            <div className="card" style={{ padding: '28px 28px 24px' }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
                letterSpacing: '0.08em', fontFamily: 'DM Sans, sans-serif', marginBottom: 16,
              }}>
                Documents{record.documents?.length ? ` (${record.documents.length})` : ''}
              </div>

              {record.documents && record.documents.length > 0 ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {record.documents.map((doc, i) => {
                      const meta = DOC_TYPE_META[doc.documentType] || DOC_TYPE_META['REPORT'];
                      return (
                        <div key={doc.id || i} style={{
                          display: 'flex', alignItems: 'center', gap: 14,
                          padding: '14px 16px', borderRadius: 14,
                          background: meta.bg, border: `1px solid ${meta.border}`,
                        }}>
                          <span style={{ fontSize: 24, flexShrink: 0 }}>{meta.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 13.5, fontWeight: 600, color: '#1e1b4b',
                              fontFamily: 'DM Sans, sans-serif',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {doc.fileName || 'Document'}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                              {doc.uploadedAt
                                ? new Date(doc.uploadedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                : ''}
                            </div>
                          </div>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 9999,
                            background: '#fff', color: meta.color, border: `1px solid ${meta.border}`,
                            flexShrink: 0, fontFamily: 'DM Sans, sans-serif',
                          }}>
                            {meta.label}
                          </span>
                          {doc.presignedUrl && (
                            <a
                              href={doc.presignedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: 12.5, fontWeight: 600, color: meta.color,
                                background: '#fff', border: `1px solid ${meta.border}`,
                                borderRadius: 9, padding: '6px 12px', cursor: 'pointer',
                                textDecoration: 'none', flexShrink: 0,
                                fontFamily: 'DM Sans, sans-serif', transition: 'opacity .12s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                              ↓ Download
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{
                    fontSize: 11.5, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif', marginTop: 14,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    ⏱ Download links expire 60 minutes after page load
                  </div>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px 0',
                  gap: 10,
                }}>
                  <span style={{ fontSize: 36 }}>📭</span>
                  <div style={{
                    fontSize: 13.5, fontWeight: 600, color: '#9ca3af',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    No documents attached
                  </div>
                  <div style={{
                    fontSize: 12, color: '#c4c9d4', fontFamily: 'DM Sans, sans-serif',
                    textAlign: 'center', maxWidth: 200,
                  }}>
                    The doctor did not upload any files for this consultation.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConsultationResultPage;